import ConnectBar from '@econsult/econsult-ui/dist/components/ConnectBar';
import Button from 'components/Button';
import GenericModalTrigger from 'components/Modal/GenericModalTrigger';
import * as ModalStyle from 'components/Modal/styles';
import Page from 'components/Page';
import Summary from 'components/Summary/Summary';
import SaveToRecordModal from 'components/Toolbar/SaveToRecordModal';
import { STRINGS } from 'config';
import { SAVING } from 'model/toolbar/saveToRecord';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { triggerToolbarGetCurrentPatient } from 'store/action/toolbar/getCurrentPatient';
import { useAppDispatch, useAppState } from 'store/configureStore';
import { getButtonProps, getPatientMatchingChip, getSaveToRecordState } from 'util/helper';
import { Heading, WarningText } from './styles';

interface StartAgainButtonProps {
  handleConfirmNewConsultation: () => void;
  isModalOpenProp?: boolean;
  setShowModalFuncProp: (arg: boolean) => void;
}

const StartAgainButton: React.FC<StartAgainButtonProps> = ({
  handleConfirmNewConsultation,
  isModalOpenProp,
  setShowModalFuncProp,
}) => {
  const closeModal = () => setShowModalFuncProp(false);
  return (
    <GenericModalTrigger trigger={<></>} title="Start new consultation" isModalOpenProp={isModalOpenProp}>
      {() => (
        <>
          <ModalStyle.ModalMessage data-automation="Modal-Message-New-Consultation">
            The current summary will be lost permanently. Are you sure you want to start a new consultation?
          </ModalStyle.ModalMessage>
          <ModalStyle.ButtonContainer data-automation="Modal-Button-Container">
            <Button
              id="Modal-Cancel-New-Consultation"
              data-automation="Modal-Cancel-New-Consultation"
              onClick={closeModal}
              buttonText="Cancel"
              icon={null}
              iconPosition="left"
              state="secondary"
              disabled={false}
            />
            <Button
              id="Modal-Confirm-New-Consultation"
              data-automation="Modal-Confirm-New-Consultation"
              onClick={() => {
                handleConfirmNewConsultation();
                closeModal();
              }}
              buttonText="Start new consultation"
              icon={null}
              iconPosition="left"
              state="primary"
              disabled={false}
            />
          </ModalStyle.ButtonContainer>
        </>
      )}
    </GenericModalTrigger>
  );
};

export default function SummaryPage() {
  const [isSaveModalOpenProp, setIsSaveModalOpen] = useState(false);
  const [isStartAgainModalOpenProp, setIsStartAgainModalOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    summary: { currentSummary },
    app: { clinicalSystemName },
    toolbar: { patient, saveToRecordStatus },
  } = useAppState();

  const { isInteropEnabled, isPatientMatched, patientMatchingChip } = getPatientMatchingChip(
    patient,
    clinicalSystemName,
  );

  const saveToRecordState = getSaveToRecordState(saveToRecordStatus);
  const showRightContainerPart = saveToRecordStatus !== SAVING;

  const handleConfirmNewConsultation = () => {
    dispatch({ type: STRINGS.REDUCERS.TOOLBAR.RESET_TOOLBAR_STATUS });
    dispatch({ type: STRINGS.REDUCERS.SNOMED.RESET_SNOMED });
    dispatch({ type: STRINGS.REDUCERS.COPILOT.RESET_COPILOT_QUERY_AND_RESPONSE });
    dispatch({ type: STRINGS.REDUCERS.SUMMARY.RESET_SUMMARY_STATE });
    dispatch({ type: STRINGS.REDUCERS.TRANSCRIPTION.RESET_TRANSCRIPTION });
    navigate('/');
  };

  return (
    <Page>
      <Heading>Post-consultation review</Heading>
      <ConnectBar
        patientMatchingChip={patientMatchingChip}
        onClickReload={() => triggerToolbarGetCurrentPatient(dispatch)}
        handleOnClickSaveAction={() => setIsSaveModalOpen(true)}
        handleOnClickStartAgainAction={() => setIsStartAgainModalOpen(true)}
        isInteropEnabled={isInteropEnabled}
        showRightContainer={showRightContainerPart}
        isSearchingPatient={false}
        buttonProps={getButtonProps(
          isPatientMatched,
          saveToRecordState,
          patientMatchingChip.matchState,
          patientMatchingChip.matchData,
        )}
        isSaveButtonDisabled={
          patient === 'FETCHING' || patient === null || patient.status === 400 || Boolean(currentSummary?.length === 0)
        }
      />
      <SaveToRecordModal
        summary={currentSummary}
        isModalOpenProp={isSaveModalOpenProp}
        setShowModalFuncProp={setIsSaveModalOpen}
      />
      <StartAgainButton
        handleConfirmNewConsultation={handleConfirmNewConsultation}
        isModalOpenProp={isStartAgainModalOpenProp}
        setShowModalFuncProp={setIsStartAgainModalOpen}
      />
      <Summary />
      <WarningText>
        <strong>Important:</strong> If you start a new consultation, the current summary will be lost.
      </WarningText>
    </Page>
  );
}
